import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Upload, Download, Trash2, FileText, ArrowLeft } from "lucide-react";
import api, { apiErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

/**
 * Admin > Bulk Bhega CSV
 * - Download a sample CSV template (with the exact 5 columns expected).
 * - Upload a CSV to seed: Bhega joins, intent-dates and chat messages on real products.
 * - List past uploads with row / Bhega / message counts.
 * - Delete a previous upload — wipes only the records tagged with that upload id.
 */
export default function AdminBhegaCsvPage() {
  const { user, ready } = useAuth();
  const [uploads, setUploads] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/bhega-csv");
    setUploads(r.data.uploads || []);
  };

  useEffect(() => {
    if (ready && user?.role === "admin") load();
  }, [ready, user]);

  if (!ready) return <div className="max-w-7xl mx-auto px-4 py-20 text-muted-foreground">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const downloadSample = async () => {
    try {
      const res = await api.get("/admin/bhega-csv/sample", { responseType: "text" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "bhega_seed_sample.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Sample CSV download failed"));
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setLastResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/admin/bhega-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastResult(res.data);
      toast.success(`Seeded ${res.data.bhega} Bhega · ${res.data.messages} messages from ${res.data.rows} row(s)`);
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Upload failed"));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const onDelete = async (upload) => {
    if (!window.confirm(`Delete every seeded Bhega / message / date from "${upload.filename}"? This cannot be undone.`)) return;
    try {
      const res = await api.delete(`/admin/bhega-csv/${upload.id}`);
      toast.success(`Removed ${res.data.deleted_bhega_joins} Bhega · ${res.data.deleted_messages} messages`);
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Delete failed"));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/admin" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3">
        <ArrowLeft size={12} /> Back to Admin
      </Link>
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand">Admin · Seed Tools</div>
      <h1 className="t-page">Bulk Bhega CSV</h1>
      <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
        Upload a CSV to inflate Bhega counts and seed negotiation-room chats on real products. Every row inserts <strong>N</strong> seed buyers
        on the given product + intended date + state-scope, plus one chat message per pipe-separated <code>Communication</code>. Everything is tagged
        with the upload ID so it can be deleted in one click.
      </p>

      {/* CSV format help */}
      <div className="mt-6 border border-border rounded-none bg-[#FFBE06]/5 p-5">
        <div className="font-semibold mb-2">CSV columns (exact order)</div>
        <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
          <li><strong>Product</strong> — must match an existing product (e.g. <em>Royal Enfield Classic 350</em>, <em>iPhone 17e</em>, <em>Arvind Aqua City</em>).</li>
          <li><strong>Number of Bhega</strong> — integer; creates that many seed buyers.</li>
          <li><strong>Month</strong> — intended purchase month <code>YYYY-MM</code> within the next 12 months.</li>
          <li><strong>Communication</strong> — chat messages separated by <code>|</code> (pipe). Each becomes a message by a distinct seed buyer.</li>
          <li><strong>City/State</strong> — e.g. <em>&quot;Ahmedabad, Gujarat&quot;</em>. Drives the negotiation-room scope (per-state for autos/electronics, global for real-estate).</li>
        </ul>
      </div>

      {/* Actions */}
      <div className="mt-5 flex flex-wrap gap-3">
        <button onClick={downloadSample} className="btn-outline text-sm" data-testid="bhega-csv-download-sample">
          <Download size={14} /> Download sample CSV
        </button>
        <label className="btn-primary text-sm cursor-pointer" data-testid="bhega-csv-upload-label">
          <Upload size={14} /> {busy ? "Uploading…" : "Upload Bhega CSV"}
          <input type="file" accept=".csv,text/csv" hidden disabled={busy} onChange={onUpload} data-testid="bhega-csv-upload-input" />
        </label>
      </div>

      {/* Last upload result */}
      {lastResult && (
        <div className="mt-5 border border-border bg-card p-4 rounded-none" data-testid="bhega-csv-last-result">
          <div className="font-semibold mb-1 text-sm">Last upload — {lastResult.filename}</div>
          <div className="text-xs text-muted-foreground">
            Rows processed: <strong>{lastResult.rows}</strong> · Bhega added: <strong>{lastResult.bhega}</strong> · Messages: <strong>{lastResult.messages}</strong> · Intent months: <strong>{lastResult.intent_dates}</strong>
          </div>
          {lastResult.errors && lastResult.errors.length > 0 && (
            <div className="mt-2 text-xs text-[var(--bhega-error)]">
              <div className="font-semibold">Skipped rows:</div>
              <ul className="list-disc pl-5">
                {lastResult.errors.slice(0, 10).map((e, i) => (
                  <li key={i}>Row {e.row}: {e.error}</li>
                ))}
                {lastResult.errors.length > 10 && <li>… and {lastResult.errors.length - 10} more</li>}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Past uploads */}
      <h2 className="mt-10 font-heading font-bold text-xl">Past uploads</h2>
      <div className="mt-3 border border-border rounded-none overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-foreground/5 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3">File</th>
              <th className="text-left px-4 py-3">Uploaded</th>
              <th className="text-left px-4 py-3">Rows</th>
              <th className="text-left px-4 py-3">Bhega</th>
              <th className="text-left px-4 py-3">Messages</th>
              <th className="text-right px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {uploads.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground" data-testid="bhega-csv-uploads-empty">No CSV uploads yet.</td></tr>
            ) : (
              uploads.map((u) => (
                <tr key={u.id} className="border-t border-border" data-testid={`bhega-csv-row-${u.id}`}>
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-2 font-medium">
                      <FileText size={14} className="text-brand" />
                      {u.filename}
                    </div>
                    {u.uploaded_by && <div className="text-xs text-muted-foreground mt-0.5">by {u.uploaded_by}</div>}
                  </td>
                  <td className="px-4 py-3 align-top text-xs">{new Date(u.uploaded_at).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 align-top">{u.rows}</td>
                  <td className="px-4 py-3 align-top font-semibold">{u.bhega}</td>
                  <td className="px-4 py-3 align-top">{u.messages}</td>
                  <td className="px-4 py-3 align-top text-right">
                    <button
                      onClick={() => onDelete(u)}
                      className="text-xs text-[var(--bhega-error)] underline inline-flex items-center gap-1"
                      data-testid={`bhega-csv-delete-${u.id}`}
                    >
                      <Trash2 size={12} /> Delete entries
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
